import { createResource, createSignal, For, Show } from "solid-js";
import { A } from "@solidjs/router";
import type {
  BlockSeries,
  ReportBlock,
  ReportInput,
} from "@energyplatform/client";
import { api } from "../services.ts";
import { t } from "../i18n.ts";
import { scope, siteTree } from "../context.ts";
import { inputOf } from "./ReportWizard.tsx";
import { ErrorBox } from "./ErrorBox.tsx";
import { Dialog, useAction } from "./ui.tsx";

const KINDS = ["chart", "table", "kpis"] as const;
const NEW = "__new";

/**
 * "Dodaj do raportu": what is on the screen (the chosen points, or a meter's quantities and phases) becomes a block of a
 * report — a chart, a table or headline figures — computed for the report's period at every generation. It goes into
 * an existing report or a new report of one's own.
 */
export function AddToReport(props: {
  series: BlockSeries[];
  title: string;
  onClose: () => void;
}) {
  const [reports] = createResource(() => api.reports.list());
  const [target, setTarget] = createSignal(NEW);
  const [name, setName] = createSignal(
    t("reports.add.newName", { name: scope()?.name ?? "" }),
  );
  const [title, setTitle] = createSignal(props.title);
  const [kind, setKind] = createSignal<ReportBlock["kind"]>("chart");
  const [price, setPrice] = createSignal(false);
  const [done, setDone] = createSignal<string>();
  const [run, error, busy] = useAction();
  const save = () =>
    void run(async () => {
      const block: ReportBlock = {
        id: `b${Date.now().toString(36)}`,
        title: title().trim() || props.title,
        kind: kind(),
        series: [
          ...props.series,
          ...(price()
            ? [
                {
                  source: "tge",
                  pointId: null,
                  attribute: null,
                  channel: null,
                } as BlockSeries,
              ]
            : []),
        ].slice(0, 8),
      };
      if (target() === NEW) {
        const input: ReportInput = {
          name: name().trim() || block.title,
          template: "custom",
          nodeId: scope()?.id ?? siteTree()?.nodes[0]?.id ?? "",
          period: "previous-week",
          comparison: "none",
          sections: [],
          formats: ["pdf", "xlsx"],
          landscape: false,
          schedule: "manual",
          recipients: [],
          askComment: false,
          blocks: [block],
        };
        const saved = await api.reports.create(input);
        setDone(saved.name);
      } else {
        const report = reports()!.find((r) => r.id === target())!;
        const input = inputOf(report);
        await api.reports.update(report.id, {
          ...input,
          blocks: [...(input.blocks ?? []), block],
        });
        setDone(report.name);
      }
      api.invalidate("/api/v1/reports");
    });
  return (
    <Dialog
      title={t("reports.add.title")}
      onClose={props.onClose}
      footer={
        <Show
          when={!done()}
          fallback={
            <>
              <A class="btn" href="/reports" onClick={() => props.onClose()}>
                {t("reports.add.open")}
              </A>
              <button
                type="button"
                class="btn primary"
                onClick={() => props.onClose()}
              >
                {t("common.close")}
              </button>
            </>
          }
        >
          <button type="button" class="btn" onClick={() => props.onClose()}>
            {t("common.cancel")}
          </button>
          <button
            type="button"
            class="btn primary"
            disabled={busy() || props.series.length === 0}
            onClick={save}
          >
            {t("reports.add.save")}
          </button>
        </Show>
      }
    >
      <Show
        when={!done()}
        fallback={
          <p style={{ margin: 0 }}>
            {t("reports.add.done", { name: done()! })}
          </p>
        }
      >
        <div style={{ display: "grid", gap: "12px" }}>
          <div class="field">
            <label for="atrTitle">{t("reports.add.blockTitle")}</label>
            <input
              id="atrTitle"
              value={title()}
              maxLength={120}
              onInput={(e) => setTitle(e.currentTarget.value)}
            />
          </div>
          <div class="field">
            <span class="eyebrow">{t("reports.add.kind")}</span>
            <div class="seg" role="group">
              <For each={KINDS}>
                {(k) => (
                  <button
                    type="button"
                    aria-pressed={kind() === k}
                    onClick={() => setKind(k)}
                  >
                    {t(`reports.add.kinds.${k}`)}
                  </button>
                )}
              </For>
            </div>
            <span class="hint">{t(`reports.add.kindHint.${kind()}`)}</span>
          </div>
          <label class="check">
            <input
              type="checkbox"
              checked={price()}
              onChange={(e) => setPrice(e.currentTarget.checked)}
            />
            <span>{t("reports.add.price")}</span>
          </label>
          <div class="field">
            <label for="atrTarget">{t("reports.add.target")}</label>
            <select
              id="atrTarget"
              value={target()}
              onChange={(e) => setTarget(e.currentTarget.value)}
            >
              <option value={NEW}>{t("reports.add.newReport")}</option>
              <For each={reports() ?? []}>
                {(r) => <option value={r.id}>{r.name}</option>}
              </For>
            </select>
          </div>
          <Show when={target() === NEW}>
            <div class="field">
              <label for="atrName">{t("reports.add.name")}</label>
              <input
                id="atrName"
                value={name()}
                maxLength={120}
                onInput={(e) => setName(e.currentTarget.value)}
              />
            </div>
          </Show>
          <p class="hint" style={{ margin: 0 }}>
            {t("reports.add.hint")}
          </p>
          <Show when={error()}>
            <ErrorBox error={error()} />
          </Show>
        </div>
      </Show>
    </Dialog>
  );
}
